/**
 * Config API functions
 * Public endpoints that don't require authentication
 */
import { getBaseApiUrl } from './core';
import { ENDPOINTS } from './endpoints';
import logger from '../utils/logger';

/**
 * Fetch feature flags from the server (public endpoint, no auth needed)
 * @returns {Promise<Object>} Feature flags object
 */
export async function getFeatureFlags() {
  const url = getBaseApiUrl() + ENDPOINTS.CONFIG.FEATURES;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error fetching feature flags:', error);
    // Return default flags on error (fail-safe defaults)
    return {
      chat: true,
      journal: true,
      charts: true,
      multiAsset: true,
      llmRules: true,
      admin: true,
      validationOnlyMode: false,
      productMode: 'full',
    };
  }
}

/**
 * Fetch feature flags resolved for the current user (with overrides applied)
 * @param {string} idToken - Firebase auth token
 * @returns {Promise<Object>} Feature flags object with user-specific overrides
 */
export async function getResolvedFeatureFlags(idToken) {
  const url = getBaseApiUrl() + ENDPOINTS.CONFIG.FEATURES_RESOLVED;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': idToken,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error fetching resolved feature flags:', error);
    // Fall back to public flags on error
    return getFeatureFlags();
  }
}

/**
 * Refresh feature flags cache (admin only in practice)
 * @returns {Promise<Object>} Updated feature flags
 */
export async function refreshFeatureFlags() {
  const url = getBaseApiUrl() + ENDPOINTS.CONFIG.FEATURES_REFRESH;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error refreshing feature flags:', error);
    throw error;
  }
}

/**
 * Fetch current user's subscription info including tier, usage, and subscription details
 * @param {string} idToken - Firebase auth token
 * @returns {Promise<Object>} { tier, usage, subscription }
 */
export async function getMySubscription(idToken) {
  const url = getBaseApiUrl() + ENDPOINTS.CONFIG.MY_SUBSCRIPTION;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': idToken,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error fetching subscription:', error);
    return null;
  }
}

/**
 * Fetch current user's usage status
 * @param {string} idToken - Firebase auth token
 * @returns {Promise<Object>} UsageStatus object
 */
export async function getMyUsage(idToken) {
  const url = getBaseApiUrl() + ENDPOINTS.CONFIG.MY_USAGE;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': idToken,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error fetching usage:', error);
    return null;
  }
}
